import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import IntelexFrame from '../intelex/IntelexFrame';
import ArrowIcon from '../intelex/ArrowIcon';
import { dataList, displayDate, intelexRequest, reportDetailPath } from '../intelex/api';

function reportState(value) {
  return { pending: 'Pending review', accepted: 'Accepted', rejected: 'Rejected', invalidated: 'PIREP invalidated' }[value] || 'Pending review';
}

function ReportHub({ user }) {
  const [templates, setTemplates] = useState([]);
  const [reports, setReports] = useState([]);
  const [state, setState] = useState({ loading: true, error: '' });
  const [query, setQuery] = useState('');

  useEffect(() => {
    let current = true;
    Promise.all([
      intelexRequest('/api/portal/report-templates'),
      intelexRequest('/api/portal/reports').catch(() => ({ data: [] })),
    ]).then(([templatePayload, reportPayload]) => {
      if (!current) return;
      setTemplates(dataList(templatePayload).filter((template) => template.status !== 'draft' && template.status !== 'archived'));
      setReports(dataList(reportPayload).slice(0, 5));
      setState({ loading: false, error: '' });
    }).catch((error) => {
      if (current) setState({ loading: false, error: error.message || 'Report forms could not be loaded.' });
    });
    return () => { current = false; };
  }, []);

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return templates;
    return templates.filter((template) => `${template.title} ${template.description || ''} ${template.category || ''}`.toLowerCase().includes(search));
  }, [query, templates]);

  return (
    <div className="intelex-main intelex-hub">
      <header className="intelex-page-heading">
        <div><h1>Safety reports</h1><p>Select the report that best matches the event or concern.</p></div>
        {user.role === 'admin' && <Link className="intelex-secondary-button" to="/interep/admin/templates">Manage report forms</Link>}
      </header>

      {state.error && <div className="intelex-alert" role="alert">{state.error}</div>}

      <section className="intelex-panel intelex-template-panel" aria-labelledby="available-reports-title">
        <div className="intelex-panel-heading">
          <div><h2 id="available-reports-title">Create a report</h2><span>{filtered.length} available</span></div>
          <label className="intelex-search"><span className="sr-only">Search report types</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search report types" /></label>
        </div>
        {state.loading ? <div className="intelex-loading" role="status">Loading report forms…</div> : filtered.length ? (
          <div className="intelex-template-grid">
            {filtered.map((template) => <Link className="intelex-template-card" key={template.id} to={`/interep/report/new/${template.id}`}>
              <h3>{template.title}</h3>
              {template.description && <p>{template.description}</p>}
              <div><span>{template.requiresPirep ? 'A flight must be selected' : 'No linked flight required'}</span><ArrowIcon /></div>
            </Link>)}
          </div>
        ) : <div className="intelex-empty"><strong>No matching report forms</strong><p>Try a different search.</p></div>}
      </section>

      <section className="intelex-panel intelex-recent-reports" aria-labelledby="recent-reports-title">
        <div className="intelex-panel-heading"><div><h2 id="recent-reports-title">Your recent reports</h2><span>Reports are visible only to you and authorised safety staff.</span></div><Link to="/interep/reports">View all</Link></div>
        {reports.length ? <div className="intelex-report-list">{reports.map((report) => <Link key={report.id} to={reportDetailPath(report)}>
          <div><strong>{report.eventNumber || report.reference || `Report #${report.id}`}</strong><span>{report.template?.title || report.templateTitle || report.title || report.template?.category || report.category || 'Safety report'}</span></div>
          <time>{displayDate(report.submittedAt || report.createdAt)}</time>
          <span className="intelex-list-state">{reportState(report.reviewStatus)}</span>
        </Link>)}</div> : !state.loading && <div className="intelex-empty compact"><p>You have not submitted a report yet.</p></div>}
      </section>
    </div>
  );
}

export default function IntelexHomePage() {
  return <IntelexFrame active="home" title="Safety reports">{({ user }) => <ReportHub user={user} />}</IntelexFrame>;
}
